/**
 * Geometrie des Netzdiagramms.
 *
 * Bewusst handgerechnet statt mit einer Diagrammbibliothek: ein Netz ueber
 * zwei bis acht Achsen sind ein paar Zeilen Trigonometrie, waehrend eine
 * Bibliothek rund 100 kB kostet und sich gegen die Bereichsfarbe pro Achse
 * wehrt. Genauso wie die Heatmap bei den Gewohnheiten kein Diagramm ist.
 *
 * Koordinatensystem: Mittelpunkt ist (0, 0), y waechst nach UNTEN (SVG).
 * Die erste Achse zeigt nach oben, weitere folgen im Uhrzeigersinn.
 */

export interface Point {
  x: number;
  y: number;
}

/** Die Skala des Lebensrads: 1 bis 10. */
export const SCALE_MIN = 1;
export const SCALE_MAX = 10;

/** Winkel einer Achse in Radiant. Achse 0 zeigt nach oben. */
export function axisAngle(index: number, count: number): number {
  if (count <= 0) return -Math.PI / 2;
  return -Math.PI / 2 + (index * 2 * Math.PI) / count;
}

/** Punkt auf einer Achse, `distance` Pixel vom Mittelpunkt entfernt. */
export function pointAt(index: number, count: number, distance: number): Point {
  const winkel = axisAngle(index, count);
  return {
    x: round(distance * Math.cos(winkel)),
    y: round(distance * Math.sin(winkel)),
  };
}

/**
 * Die Ecken des Bewertungspolygons.
 *
 * Wert 0 liegt exakt im Mittelpunkt, Wert `SCALE_MAX` exakt auf dem Radius.
 * Werte ausserhalb der Skala werden geklemmt - ein verrutschter Regler darf
 * nicht aus dem Diagramm herauszeichnen.
 */
export function radarPoints(values: number[], radius: number): Point[] {
  return values.map((value, i) =>
    pointAt(i, values.length, (clamp(value) / SCALE_MAX) * radius),
  );
}

/** Die Ecken eines Gitterrings auf Hoehe `level` (1..SCALE_MAX). */
export function ringPoints(count: number, radius: number, level: number): Point[] {
  const distance = (clamp(level) / SCALE_MAX) * radius;
  return Array.from({ length: count }, (_, i) => pointAt(i, count, distance));
}

/** Punkte als geschlossener SVG-Pfad. Leere Liste ergibt einen leeren Pfad. */
export function polygonPath(points: Point[]): string {
  if (points.length === 0) return '';
  const [first, ...rest] = points as [Point, ...Point[]];
  return `M ${first.x} ${first.y} ${rest.map((p) => `L ${p.x} ${p.y}`).join(' ')} Z`;
}

/**
 * Ankerpunkt fuer die Achsenbeschriftung, etwas ausserhalb des Netzes.
 *
 * `anchor` sagt, wie der Text auszurichten ist: links vom Diagramm rechts
 * buendig, rechts davon links buendig - sonst ragt er ueber den Rand.
 */
export function labelPoint(index: number, count: number, radius: number): Point & {
  anchor: 'start' | 'middle' | 'end';
} {
  const p = pointAt(index, count, radius);
  const anchor = Math.abs(p.x) < radius * 0.2 ? 'middle' : p.x > 0 ? 'start' : 'end';
  return { ...p, anchor };
}

function clamp(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(SCALE_MAX, Math.max(0, value));
}

/**
 * Auf drei Nachkommastellen - SVG braucht nicht mehr, Tests lesen sich besser.
 *
 * Die Null wird ausdruecklich positiv gemacht: `0 * Math.sin(-PI/2)` ergibt
 * `-0`, und damit ist der Mittelpunkt (0, -0) statt (0, 0) - fuer SVG egal,
 * fuer jeden Vergleich nicht.
 */
function round(value: number): number {
  const gerundet = Math.round(value * 1000) / 1000;
  return gerundet === 0 ? 0 : gerundet;
}
